<template>
  <div
    v-if="pageType === 'categorie'"
    class="inter-level1">
    <BlogCategories
      :seo-inter-data="seoInterData ?? null"
      :loading="seoLoading"
      :is-logged-in="isLoggedIn" />
  </div>
  <div
    v-if="pageType === 'article'"
    class="inter-level1">
    <BlogArticle
      :article-data="articleData ?? null"
      :loading="articleLoading"
      :is-logged-in="isLoggedIn" />
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef } from 'vue'
import type { ArticleDataResponse } from '~/types/article'
import type { SeoParentRedirectState } from '~/utils/seoDeadPayloads'
import { computed, watch } from 'vue'
import { useArticleData } from '~/composables/useArticleData'
import { useSeoInterData } from '~/composables/useSeoInterData'
import { useSeoParentRedirect } from '~/composables/useSeoParentRedirect'
import { isEmptyArticleApiPayload, shouldRedirectToSeoParent } from '~/utils/seoDeadPayloads'

const { locale } = useI18n()
const route = useRoute()
useLocalePath()

// Reconstitue le tableau de segments depuis l'URL
const pathSegments: ComputedRef<string[]> = computed(() => {
  const p: string | string[] | undefined = route.params.slug
  return Array.isArray(p) ? p : p ? [p] : []
})

// Selon le nombre de segments, affiche du contenu différent
const pageType: ComputedRef<'categorie' | 'article' | undefined> = computed(() => {
  if (pathSegments.value.length === 1) return 'categorie'
  if (pathSegments.value.length === 2) return 'article'
})

const {
  seoInterData,
  loading: seoLoading,
  redirectIfDead,
} = await useSeoInterData({
  language: locale,
  pageType: 'blog-articles',
  param1: () => pathSegments.value[0],
})

const seoRedirect = redirectIfDead()
if (seoRedirect) await seoRedirect

const {
  articleData,
  loading: articleLoading,
  lastHttpStatus: articleHttp,
} = await useArticleData({
  alias: () => (pathSegments.value.length === 2 ? pathSegments.value[1] : ''),
  language: locale,
})

const articleRedirectState = computed<SeoParentRedirectState>(() => {
  if (pageType.value !== 'article') return { loading: false, isDead: false }
  const loading: boolean = articleLoading.value
  const isDead: boolean = shouldRedirectToSeoParent({
    loading,
    httpStatus: articleHttp.value,
    data: articleData.value,
    isEmptyPayload: (payload) =>
      isEmptyArticleApiPayload((payload as ArticleDataResponse).articledatas),
  })
  return { loading, isDead }
})

const { redirectIfDead: redirectArticleIfDead } = useSeoParentRedirect({
  pathSegments: () => pathSegments.value,
  sectionRoot: 'nos-conseils',
  state: () => articleRedirectState.value,
  source: 'nos-conseils/[...slug] (article)',
})

const articleRedirect = redirectArticleIfDead()
if (articleRedirect) await articleRedirect

watch(
  articleData,
  (_newVal) => {
    //console.log('[slug] articleData CHANGED:', _newVal?.articledatas?.coverPict)
  },
  { immediate: true },
)

// ── PAGE META ───────────────────────────────────────────────
definePageMeta({
  layout: 'default',
})

// ── API & AUTH ───────────────────────────────────────────────
const { isLoggedIn } = useAuth()
const { canonicalUrl } = useSeo()

switch (pageType.value) {
  case 'categorie':
    // ── SEO / HEAD ───────────────────────────────────────────────
    useSeoMeta({
      title: computed(() => seoInterData.value?.seodatas.title),
      description: computed(() => seoInterData.value?.seodatas.description),
      ogTitle: computed(() => seoInterData.value?.seodatas.h1),
      ogDescription: computed(() => seoInterData.value?.seodatas.introduction),
      ogType: 'website',
    })

    // JSON-LD
    useHead({
      script: computed(() => [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: seoInterData.value?.seodatas.h1,
            description: seoInterData.value?.seodatas.introduction,
            url: canonicalUrl.value,
          }),
        },
      ]),
    })

    break

  case 'article':
    // ── SEO / HEAD ───────────────────────────────────────────────
    useSeoMeta({
      title: computed(() => articleData.value?.articledatas.seoTitle),
      description: computed(() => articleData.value?.articledatas.seoDescription),
      ogTitle: computed(() => articleData.value?.articledatas.seoTitle),
      ogDescription: computed(() => articleData.value?.articledatas.seoDescription),
      ogType: 'website',
    })

    // JSON-LD
    useHead({
      script: computed(() => [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: articleData.value?.articledatas.h1,
            description: articleData.value?.articledatas.seoDescription,
            url: canonicalUrl.value,
          }),
        },
      ]),
    })

    break
}
</script>

<style scoped>
/* ════════════════════════════════════════════════════════════
   STYLES LEVEL
   ════════════════════════════════════════════════════════════ */

/* ── VARIABLES LOCALES ─────────────────────────────────────────────── */
:global(body) {
  background-color: var(--bg-base);
}

/* ── CONTENT HTML ─────────────────────────────────────────────── */
</style>
